export type TimeRangeKey =
  | "this-month"
  | "last-month"
  | "this-quarter"
  | "last-quarter"
  | "ytd"
  | "all";

export type Granularity = "weekly" | "monthly";

export type StatsFilter = {
  range: TimeRangeKey;
  granularity: Granularity;
};

export type TimeRange = {
  startInclusive: number | null;
  endExclusive: number;
};

export type TrendBucket = {
  startInclusive: number;
  endExclusive: number;
};

export const TIME_RANGE_OPTIONS: ReadonlyArray<{
  key: TimeRangeKey;
  label: string;
}> = [
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
  { key: "this-quarter", label: "This quarter" },
  { key: "last-quarter", label: "Last quarter" },
  { key: "ytd", label: "Year to date" },
  { key: "all", label: "All time" },
];

export const GRANULARITY_OPTIONS: ReadonlyArray<{
  key: Granularity;
  label: string;
}> = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

export const DEFAULT_STATS_FILTER: StatsFilter = {
  range: "ytd",
  granularity: "monthly",
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

const MAX_BUCKETS = 60;

export const parseTimeRangeKey = (value: string | null): TimeRangeKey | null =>
  TIME_RANGE_OPTIONS.find((o) => o.key === value)?.key ?? null;

export const getRangeLabel = (range: TimeRangeKey): string =>
  TIME_RANGE_OPTIONS.find((o) => o.key === range)?.label ?? range;

export const parseGranularity = (value: string | null): Granularity | null =>
  GRANULARITY_OPTIONS.find((o) => o.key === value)?.key ?? null;

const startOfMonth = (at: number, offsetMonths = 0): number => {
  const d = new Date(at);
  return new Date(d.getFullYear(), d.getMonth() + offsetMonths, 1).getTime();
};

const startOfQuarter = (at: number, offsetQuarters = 0): number => {
  const d = new Date(at);
  const quarter = Math.floor(d.getMonth() / 3) + offsetQuarters;
  return new Date(d.getFullYear(), quarter * 3, 1).getTime();
};

const startOfYear = (at: number, offsetYears = 0): number =>
  new Date(new Date(at).getFullYear() + offsetYears, 0, 1).getTime();

/**
 * `endExclusive` is the end of the calendar period rather than `now`, so a scan
 * recorded today still counts towards "this month". Clamping it to `now` here
 * would drop today's work from the period it belongs to; anything user-facing
 * clamps instead via `visibleEnd`.
 */
export const getRange = (range: TimeRangeKey, now: number): TimeRange => {
  switch (range) {
    case "this-month":
      return {
        startInclusive: startOfMonth(now),
        endExclusive: startOfMonth(now, 1),
      };
    case "last-month":
      return {
        startInclusive: startOfMonth(now, -1),
        endExclusive: startOfMonth(now),
      };
    case "this-quarter":
      return {
        startInclusive: startOfQuarter(now),
        endExclusive: startOfQuarter(now, 1),
      };
    case "last-quarter":
      return {
        startInclusive: startOfQuarter(now, -1),
        endExclusive: startOfQuarter(now),
      };
    case "ytd":
      return {
        startInclusive: startOfYear(now),
        endExclusive: startOfYear(now, 1),
      };
    case "all":
      return {
        startInclusive: null,
        endExclusive: Number.POSITIVE_INFINITY,
      };
  }
};

const visibleEnd = (range: TimeRange, now: number): number =>
  Math.min(range.endExclusive, now);

export const isWithinRange = (
  at: string | number,
  range: TimeRange,
): boolean => {
  const t = typeof at === "number" ? at : new Date(at).getTime();
  if (!Number.isFinite(t)) return false;
  if (range.startInclusive !== null && t < range.startInclusive) return false;
  return t < range.endExclusive;
};

export const getTrendBuckets = (
  filter: StatsFilter,
  now: number,
  earliest: number | null,
): TrendBucket[] => {
  const range = getRange(filter.range, now);
  const end = visibleEnd(range, now);
  const start = range.startInclusive ?? earliest ?? startOfMonth(end, -11);
  if (start >= end) return [];

  const buckets: TrendBucket[] = [];
  let cursor = end;

  while (cursor > start && buckets.length < MAX_BUCKETS) {
    const previous =
      filter.granularity === "weekly"
        ? cursor - WEEK_MS
        : startOfMonth(cursor - 1);
    const bucketStart = Math.max(start, previous);
    buckets.unshift({ startInclusive: bucketStart, endExclusive: cursor });
    cursor = bucketStart;
  }

  return buckets;
};

export const formatRangeLabel = (
  range: TimeRangeKey,
  now: number,
): string | null => {
  const bounds = getRange(range, now);
  if (bounds.startInclusive === null) return null;
  const format = (at: number) =>
    new Date(at).toLocaleDateString([], {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  const lastDay = visibleEnd(
    { ...bounds, endExclusive: bounds.endExclusive - 1 },
    now,
  );
  return `${format(bounds.startInclusive)} – ${format(lastDay)}`;
};
